/**
 * Build the wire payload for POST /api/sessions from the in-memory
 * ActiveSession the gameplay screens accumulate. Lives in /lib so the
 * CelebrationScreen + (eventually) mid-session resume can both call it
 * without duplicating the shape.
 *
 * The encoding flattens the SessionContext's three per-phase records
 * into the single `attempts` array spec §9 expects, one row per
 * (word, phase) tuple. matched/threeXCompleted are derived here so
 * the backend can stay dumb about scoring rules.
 */
import type { ActiveSession } from "@/context/SessionContext";
import type { SessionInput } from "@/types/data";

import { attemptMatches, missedFromFirstPass } from "./spellingCompare";

export interface BuildSessionPayloadOptions {
  startedAt: string;
  completedAt: string;
  timeSpentSeconds: number;
  /** false for past-week reviews (spec §5.4) — the engines short-circuit
   *  on this flag so the session row persists but doesn't move the
   *  streak / unlock stickers / tick mastery counters. */
  countsTowardProgress: boolean;
}

export function buildSessionPayload(
  session: ActiveSession,
  opts: BuildSessionPayloadOptions,
): SessionInput {
  const missed = missedFromFirstPass(session.words, session.firstPassAttempts);

  const attempts: SessionInput["attempts"] = [];

  // First pass — one row per word, matched per the §4.2 rule.
  for (const w of session.words) {
    const attempt = session.firstPassAttempts[w.id] ?? "";
    attempts.push({
      wordEntryId: w.id,
      phase: "first_pass",
      attempt,
      matched: attemptMatches(attempt, w.spelling),
    });
  }

  // Second chance — only for first-pass-missed words. Skip words with
  // no recorded attempt (shouldn't happen after a full second-chance
  // run, but defensive against partial state).
  for (const w of missed) {
    const attempt = session.secondChanceAttempts[w.id];
    if (attempt === undefined) continue;
    attempts.push({
      wordEntryId: w.id,
      phase: "second_chance",
      attempt,
      matched: attemptMatches(attempt, w.spelling),
    });
  }

  // 3x-write — one row per first-pass-missed word, with the 0..3 count.
  for (const w of missed) {
    attempts.push({
      wordEntryId: w.id,
      phase: "three_x_write",
      threeXCompleted: session.threeXCompletions[w.id] ?? 0,
    });
  }

  const firstPassScore = session.words.reduce((n, w) => {
    const a = session.firstPassAttempts[w.id] ?? "";
    return n + (attemptMatches(a, w.spelling) ? 1 : 0);
  }, 0);

  return {
    weekId: session.weekId,
    startedAt: opts.startedAt,
    completedAt: opts.completedAt,
    firstPassScore,
    countsTowardProgress: opts.countsTowardProgress,
    timeSpentSeconds: opts.timeSpentSeconds,
    attempts,
  };
}
